import { forwardRef } from "react";

import { classNames } from "primereact/utils";

import {
  FontAwesomeIcon as FontAwesomeIconBase,
  FontAwesomeIconProps as FontAwesomeIconBaseProps,
} from "@fortawesome/react-fontawesome";

import "./Icon.scss";

type IconTone =
  | "default"
  | "muted"
  | "button"
  | "brand"
  | "inverse"
  | "danger"
  | "success"
  | "warning"
  | "warning-light";
type IconSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
type IconSpacing = "none" | "xs" | "sm" | "md";
type RoundedSize = "sm" | "md" | "lg";

export interface IFontAwesomeIconProps
  extends Omit<FontAwesomeIconBaseProps, "className" | "style" | "color" | "size"> {
  color?: IconTone;
  iconSize?: IconSize;
  spacingEnd?: IconSpacing;
  rounded?: boolean;
  roundedSize?: RoundedSize;
  roundedInteractive?: boolean;
}

const toneClassNameMap: Record<IconTone, string> = {
  default: "app-icon-tone--default",
  muted: "app-icon-tone--muted",
  button: "app-icon-tone--button",
  brand: "app-icon-tone--brand",
  inverse: "app-icon-tone--inverse",
  danger: "app-icon-tone--danger",
  success: "app-icon-tone--success",
  warning: "app-icon-tone--warning",
  "warning-light": "app-icon-tone--warning-light",
};

const sizeClassNameMap: Record<IconSize, string> = {
  xs: "app-icon-size--xs",
  sm: "app-icon-size--sm",
  md: "app-icon-size--md",
  lg: "app-icon-size--lg",
  xl: "app-icon-size--xl",
  "2xl": "app-icon-size--2xl",
  "3xl": "app-icon-size--3xl",
  "4xl": "app-icon-size--4xl",
  "5xl": "app-icon-size--5xl",
};

const roundedSizeClassNameMap: Record<RoundedSize, string> = {
  sm: "app-icon-round-size--sm",
  md: "app-icon-round-size--md",
  lg: "app-icon-round-size--lg",
};

export const Icon = forwardRef<SVGSVGElement, IFontAwesomeIconProps>(
  ({ color, iconSize = "md", rounded = false, roundedSize = "lg", roundedInteractive = false, ...props }, ref) => {
    const icon = (
      <FontAwesomeIconBase
        ref={ref}
        {...props}
        className={classNames("app-icon", color && toneClassNameMap[color], sizeClassNameMap[iconSize])}
      />
    );

    if (!rounded) {
      return icon;
    }

    return (
      <span
        className={classNames(
          "app-icon-round",
          roundedSizeClassNameMap[roundedSize],
          roundedInteractive && "app-icon-round--interactive",
        )}
      >
        {icon}
      </span>
    );
  },
);

Icon.displayName = "Icon";
